import { useState, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { AuthContext } from "../../context/AuthContext";
import api from "../../api/api";
import logoBranca from "../../assets/logo-transparente.png";
import styles from "./Login.module.scss";

// ─── Constants ───────────────────────────────────────────────────────────────

const ACCESS_RULES = {
  superadmin: "/painel",
};

const SWAL_BASE = {
  customClass: {
    popup: styles.swalPopup,
    confirmButton: styles.swalConfirmBtn,
  },
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

const showSuccess = (nome) =>
  Swal.fire({
    ...SWAL_BASE,
    icon: "success",
    title: "Login realizado!",
    text: `Bem-vindo, ${nome}!`,
    timer: 1500,
    showConfirmButton: false,
  });

const showError = (message) =>
  Swal.fire({
    ...SWAL_BASE,
    icon: "error",
    title: "Erro",
    text: message,
  });

const showAccessDenied = () =>
  Swal.fire({
    ...SWAL_BASE,
    icon: "error",
    title: "Acesso negado",
    text: "Você não tem permissão para acessar esta área.",
  });

// ─── Sub-components ───────────────────────────────────────────────────────────

const InputField = ({ id, label, type = "text", value, onChange, autoComplete, disabled }) => (
  <div className={styles.field}>
    <label htmlFor={id} className={styles.label}>
      {label}
    </label>
    <input
      id={id}
      type={type}
      value={value}
      onChange={onChange}
      autoComplete={autoComplete}
      disabled={disabled}
      required
      className={styles.input}
      aria-required="true"
    />
  </div>
);

// ─── Main Component ───────────────────────────────────────────────────────────

const Login = () => {
  const { login } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loginInput, setLoginInput] = useState("");
  const [senha, setSenha] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (isLoading) return;

      setIsLoading(true);

      try {
        const { data } = await api.post("/usuarios/login", {
          login: loginInput.trim(),
          senha,
        });

        const { usuario, token } = data;

        login(usuario, token);
        // NOTE: Ideally, token persistence should live inside login() in AuthContext,
        // keeping the component free of storage concerns.
        localStorage.setItem("token", token);

        const destination = ACCESS_RULES[usuario.nivel_acesso];

        if (!destination) {
          showAccessDenied();
          navigate("/login");
          return;
        }

        await showSuccess(usuario.nome);
        navigate(destination);
      } catch (err) {
        const message =
          err.response?.data?.error || "Não foi possível realizar o login. Tente novamente.";
        showError(message);
      } finally {
        setIsLoading(false);
      }
    },
    [isLoading, login, loginInput, navigate, senha]
  );

  return (
    <main className={styles.page} aria-label="Página de login">
      <div className={styles.card}>
        <div className={styles.logoWrapper}>
          <img src={logoBranca} alt="FluxoEasy" className={styles.logo} />
        </div>

        <h1 className={styles.title}>Bem-vindo de volta</h1>
        <p className={styles.subtitle}>Acesse o painel administrativo</p>

        <form
          className={styles.form}
          onSubmit={handleSubmit}
          noValidate
          aria-label="Formulário de login"
        >
          <InputField
            id="login-username"
            label="Usuário"
            value={loginInput}
            onChange={(e) => setLoginInput(e.target.value)}
            autoComplete="username"
            disabled={isLoading}
          />

          <InputField
            id="login-password"
            label="Senha"
            type="password"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            autoComplete="current-password"
            disabled={isLoading}
          />

          <button
            type="submit"
            className={styles.submitBtn}
            disabled={isLoading || !loginInput || !senha}
            aria-busy={isLoading}
          >
            {isLoading ? (
              <>
                <span className={styles.spinner} aria-hidden="true" />
                <span>Entrando...</span>
              </>
            ) : (
              "Entrar"
            )}
          </button>
        </form>
      </div>
    </main>
  );
};

export default Login;